/** @jsxImportSource preact */
import { Fragment, render, type FunctionalComponent } from "preact";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";

// -- Types --
export type CueCardPosition = "top-center" | "top-right";
export type CueCardDirection = "top-to-bottom" | "right-to-left";
export type CueCardTheme = "glass" | "light" | "dark";
export type PersonalizationStatus =
  | "resting"
  | "starting"
  | "success"
  | "error";

export type KeyboardModifier = "ctrl" | "alt" | "shift" | "meta";

export interface CueCardKeyboardShortcut {
  key: string;
  modifiers?: KeyboardModifier[];
}

export type LauncherActionStatus = "loading" | "ready" | "idle";

export interface LauncherAction {
  status: LauncherActionStatus;
  tooltip?: string;
  onClick?: () => void;
}

export interface PersonalizationInput {
  [key: string]: string;
}

export interface PersonalizationField {
  name: string;
  label: string;
  placeholder?: string;
  type?: "text" | "email" | "tel" | "url";
  required?: boolean;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  errorMessage?: string;
}

export interface CueCardThemeConfig {
  fontFamily?: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  subtitleColor?: string;
  primaryColor?: string;
  primaryTextColor?: string;
  borderRadius?: string;
}

export interface CueCardTextConfig {
  titleResting?: string;
  titleSuccess?: string;
  titleError?: string;
  subtitleResting?: string;
  subtitleSuccess?: string;
  subtitleError?: string;
  btnResting?: string;
  btnSuccess?: string;
  btnLoading?: string;
  btnError?: string;
  launcherLabel?: string;
  launcherLabelSuccess?: string;
  launcherLabelDeanonymized?: string;
  launcherLabelDeanonymizedSuccess?: string;
}

export type CueCardDeanonymizationProvider = "Kenobi";

export interface CueCardDeanonymizationConfig {
  provider: CueCardDeanonymizationProvider;
  timeoutMs?: number;
  logoUrlTemplate?: string;
  snitcher?: {
    spotterToken: string;
    profileId: string;
    apiEndpoint?: string;
    cdn?: string;
    namespace?: string;
  };
}

export interface DeanonymizedCompany {
  name: string;
  domain?: string;
  logoUrl?: string;
}

export interface CueCardConfig {
  position?: CueCardPosition;
  direction?: CueCardDirection;
  theme?: CueCardTheme;
  entranceDelayMs?: number;
  exitDelayMs?: number;
  fields: PersonalizationField[];
  hasPersonalization?: boolean;
  isVisible?: boolean;
  progressPct?: number;
  status?: PersonalizationStatus;
  enableTimer?: boolean;
  timerDurationMs?: number;

  // New config props
  customTheme?: CueCardThemeConfig;
  textOverrides?: CueCardTextConfig;
  enableFocusMode?: boolean;
  focusBlurIntensity?: string;
  focusDelayMs?: number;
  enableLauncher?: boolean;
  enableClickSheen?: boolean;
  showLauncherLogo?: boolean;
  launcherAction?: LauncherAction;
  launcherStyles?: Record<string, string>;
  launcherShineBorder?: {
    enabled: boolean;
    color?: string | string[];
    duration?: number;
    borderWidth?: number;
  };
  showKeyboardHints?: boolean;
  showWatermark?: boolean;
  keyboardShortcut?: CueCardKeyboardShortcut;
  mountMode?: "overlay" | "inline";
  mountNode?: HTMLElement;

  // Undo toggle feature
  enableUndoToggle?: boolean;
  isPersonalized?: boolean;
  deanonymization?: CueCardDeanonymizationConfig;

  onDismiss?: () => void;
  onExitComplete?: () => void;
  onEntranceComplete?: () => void;
  onSubmitPersonalization?: (values: PersonalizationInput) => void;
  onOpenChange?: (isOpen: boolean) => void;
  onTogglePersonalization?: () => void;
}

// -- Icons (Converted to light components) --
const ArrowRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="3"
    stroke-linecap="round"
    stroke-linejoin="round"
    width="16"
    height="16"
  >
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    width="16"
    height="16"
  >
    <path
      fillRule="evenodd"
      d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 0 1 1.04-.208Z"
      clipRule="evenodd"
    />
  </svg>
);
const XMarkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    width="16"
    height="16"
  >
    <path
      fillRule="evenodd"
      d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
      clipRule="evenodd"
    />
  </svg>
);
const LoaderIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="animate-spin"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);
const LogoIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 900 900"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="446" cy="450" r="424" fill="black" />
    <circle cx="450" cy="450" r="357" fill="#E8E8E8" />
    <ellipse
      cx="579.85"
      cy="294.451"
      rx="79.778"
      ry="108.483"
      transform="rotate(-46.2324 579.85 294.451)"
      fill="black"
    />
  </svg>
);
const SparklesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    width="14"
    height="14"
  >
    <path
      fillRule="evenodd"
      d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5ZM16.5 15a.75.75 0 0 1 .712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 0 1 0 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 0 1-1.422 0l-.395-1.183a1.5 1.5 0 0 0-.948-.948l-1.183-.395a.75.75 0 0 1 0-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0 1 16.5 15Z"
      clipRule="evenodd"
    />
  </svg>
);
const ArrowPathIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    width="16"
    height="16"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
    />
  </svg>
);

type SnitcherSpotterCompany = {
  id?: string;
  name?: string;
  domain?: string;
  industry?: string;
  location?: string;
  employee_range?: string;
  founded_year?: number;
  description?: string;
  geo?: {
    city?: string;
    country?: string;
    country_code?: string;
    region?: string;
    postal_code?: string;
    full_address?: string;
    address?: string;
  };
  profiles?: {
    linkedin?: { url?: string };
    twitter?: { url?: string };
    facebook?: { url?: string };
    crunchbase?: { url?: string };
  };
};

type SnitcherSpotterResponse = {
  type?: string;
  domain?: string;
  ip?: string;
  company?: SnitcherSpotterCompany;
};

type IdentityResolutionDetail = {
  provider?: string;
  type?: "business" | "isp" | "unknown";
  domain?: string | null;
  ip?: string;
  company?: {
    id?: string;
    name?: string;
    domain?: string;
    logoUrl?: string;
    industry?: string;
    location?: string;
    employeeRange?: string;
    foundedYear?: number;
    description?: string;
    geo?: {
      city?: string;
      country?: string;
      countryCode?: string;
      region?: string;
      postalCode?: string;
      address?: string;
    };
    profiles?: {
      linkedin?: string;
      twitter?: string;
      facebook?: string;
      crunchbase?: string;
    };
  };
  raw?: unknown;
};

declare global {
  interface Window {
    SpotterSettings?: {
      token?: string;
      callback?: (response: SnitcherSpotterResponse) => void;
    };
    Snitcher?: unknown;
    __kenobiIdentityResolution?: unknown;
    __kenobiSnitcherLoaded?: boolean;
    __kenobiSnitcherLoading?: boolean;
  }
}

const IDENTITY_RESOLVED_EVENT = "kenobi:identity-resolved";
const KENOBI_SNITCHER_SCRIPT_ID = "kenobi-snitcher";
const DEFAULT_DEANON_TIMEOUT_MS = 1600;

const dispatchCueCardEvent = (
  eventName: string,
  detail: Record<string, unknown>
) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(`kenobi:${eventName}`, {
      detail,
      bubbles: true,
    })
  );
};

const resolveDeanonymizedCompany = (
  detail?: IdentityResolutionDetail | null
): DeanonymizedCompany | null => {
  if (!detail || detail.type !== "business") return null;
  const company = detail.company;
  const name = company?.name || detail.domain || undefined;
  const domain = company?.domain || detail.domain || undefined;
  if (!name) return null;
  return {
    name,
    domain,
    logoUrl:
      company?.logoUrl ||
      (domain ? `https://logo.clearbit.com/${domain}` : undefined),
  };
};

const normalizeDomain = (value?: string) => {
  if (!value) return "";
  return value
    .replace(/^https?:\/\//i, "")
    .replace(/\/.*$/, "")
    .replace(/:\d+$/, "")
    .replace(/^www\./i, "")
    .trim()
    .toLowerCase();
};

const ensureKenobiSnitcherLoaded = (config?: CueCardDeanonymizationConfig) => {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window.__kenobiSnitcherLoaded || window.__kenobiSnitcherLoading) return;
  if (window.Snitcher || window.SpotterSettings) {
    window.__kenobiSnitcherLoaded = true;
    return;
  }
  if (document.getElementById(KENOBI_SNITCHER_SCRIPT_ID)) {
    window.__kenobiSnitcherLoaded = true;
    return;
  }

  const snitcher = config?.snitcher;
  if (!snitcher?.spotterToken || !snitcher.profileId) {
    return;
  }

  window.__kenobiSnitcherLoading = true;

  const resolvedLogoTemplate =
    config?.logoUrlTemplate || "https://logo.clearbit.com/{domain}";
  const apiEndpoint =
    snitcher.apiEndpoint ||
    `${window.location.origin}/api/snitcher-proxy/radar`;
  const cdn =
    snitcher.cdn || `${window.location.origin}/api/snitcher-proxy/cdn`;
  const namespace = snitcher.namespace || "Snitcher";

  const snitcherScript = `
    (function () {
      function spotterCb(response) {
        var rawDomain = (response.company && response.company.domain) || response.domain || "";
        var normalizedDomain = rawDomain
          .replace(/^https?:\\/\\//i, "")
          .replace(/\\/.*$/, "")
          .replace(/:\\d+$/, "")
          .replace(/^www\\./i, "");
        var identity = {
          provider: "snitcher",
          type: response.type === "business" ? "business" : "unknown",
          domain: response.domain || null,
          ip: response.ip,
          company: response.company
            ? {
                id: response.company.id,
                name: response.company.name || response.domain,
                domain: response.company.domain || response.domain,
                logoUrl: "${resolvedLogoTemplate}".replace("{domain}", normalizedDomain),
                industry: response.company.industry,
                location: response.company.location,
                employeeRange: response.company.employee_range,
                foundedYear: response.company.founded_year,
                description: response.company.description,
                geo: response.company.geo
                  ? {
                      city: response.company.geo.city,
                      country: response.company.geo.country,
                      countryCode: response.company.geo.country_code,
                      region: response.company.geo.region,
                      postalCode: response.company.geo.postal_code,
                      address:
                        response.company.geo.full_address ||
                        response.company.geo.address,
                    }
                  : undefined,
                profiles: response.company.profiles
                  ? {
                      linkedin: response.company.profiles.linkedin?.url,
                      twitter: response.company.profiles.twitter?.url,
                      facebook: response.company.profiles.facebook?.url,
                      crunchbase: response.company.profiles.crunchbase?.url,
                    }
                  : undefined,
              }
            : undefined,
          raw: response,
        };

        window.__kenobiIdentityResolution = identity;
        window.dispatchEvent(
          new CustomEvent("${IDENTITY_RESOLVED_EVENT}", {
            detail: identity,
            bubbles: true,
          })
        );
      }

      window.SpotterSettings = {
        token: "${snitcher.spotterToken}",
        callback: spotterCb,
      };

      !(function (e) {
        "use strict";
        var t = e && e.namespace;
        if (t && e.profileId && e.cdn) {
          var i = window[t];
          if (((i && Array.isArray(i)) || (i = window[t] = []), !i.initialized && !i._loaded))
            if (i._loaded) console && console.warn("[Radar] Duplicate initialization attempted");
            else {
              i._loaded = !0;
              [
                "track",
                "page",
                "identify",
                "group",
                "alias",
                "ready",
                "debug",
                "on",
                "off",
                "once",
                "trackClick",
                "trackSubmit",
                "trackLink",
                "trackForm",
                "pageview",
                "screen",
                "reset",
                "register",
                "setAnonymousId",
                "addSourceMiddleware",
                "addIntegrationMiddleware",
                "addDestinationMiddleware",
                "giveCookieConsent",
              ].forEach(function (e) {
                var a;
                i[e] =
                  ((a = e),
                  function () {
                    var e = window[t];
                    if (e.initialized) return e[a].apply(e, arguments);
                    var i = [].slice.call(arguments);
                    return i.unshift(a), e.push(i), e;
                  });
              }),
                -1 === e.apiEndpoint.indexOf("http") &&
                  (e.apiEndpoint = "https://" + e.apiEndpoint),
                (i.bootstrap = function () {
                  var t,
                    i = document.createElement("script");
                  (i.async = !0),
                    (i.type = "text/javascript"),
                    (i.id = "__radar__"),
                    i.setAttribute("data-settings", JSON.stringify(e)),
                    (i.src = [
                      -1 !== (t = e.cdn).indexOf("http") ? "" : "https://",
                      t,
                      "/releases/latest/radar.min.js",
                    ].join(""));
                  var a = document.scripts[0];
                  a.parentNode.insertBefore(i, a);
                }),
                i.bootstrap();
            }
        } else
          "undefined" != typeof console &&
            console.error("[Radar] Configuration incomplete");
        })({
            apiEndpoint: "${apiEndpoint}",
            cdn: "${cdn}",
            namespace: "${namespace}",
            profileId: "${snitcher.profileId}",
        });
      })();
  `;

  const script = document.createElement("script");
  script.id = KENOBI_SNITCHER_SCRIPT_ID;
  script.type = "text/javascript";
  script.text = snitcherScript;
  document.head?.appendChild(script);
  window.__kenobiSnitcherLoaded = true;
  window.__kenobiSnitcherLoading = false;
};

// -- Styles --
const STYLES = `
  :host { --kb-font-family: system-ui, -apple-system, sans-serif; --kb-bg-container: rgba(255, 255, 255, 0.05); --kb-border-container: rgba(255, 255, 255, 0.2); --kb-shadow-container: 0 25px 50px -12px rgba(0, 0, 0, 0.25); --kb-backdrop-blur: 16px; --kb-text-title: #ffffff; --kb-text-subtitle: rgba(255, 255, 255, 0.7); --kb-btn-dismiss-bg: rgba(0, 0, 0, 0.7); --kb-btn-dismiss-border: rgba(255, 255, 255, 0.2); --kb-btn-dismiss-text: rgba(255, 255, 255, 0.8); --kb-btn-dismiss-hover-bg: rgba(0, 0, 0, 0.9); --kb-btn-dismiss-hover-text: #ffffff; --kb-btn-trigger-bg: #ffffff; --kb-btn-trigger-text: #000000; --kb-btn-trigger-hover-bg: rgba(255, 255, 255, 0.9); --kb-progress-track: rgba(255, 255, 255, 0.2); --kb-progress-indicator: #ffffff; --kb-popover-bg: rgba(255, 255, 255, 0.05); --kb-popover-border: rgba(255, 255, 255, 0.2); --kb-popover-text: #ffffff; --kb-input-bg: transparent;   --kb-input-border: rgba(255, 255, 255, 0.3); --kb-input-text: #ffffff; --kb-input-placeholder: rgba(255, 255, 255, 0.6); --kb-form-label: #ffffff; --kb-success-color: #6ee7b7; --kb-error-text: #ef4444; --kb-focus-blur: 10px; --kb-kbd-bg: rgba(255, 255, 255, 0.15); --kb-kbd-border: rgba(255, 255, 255, 0.4); --kb-kbd-text: #ffffff; --kb-watermark-text: rgba(255, 255, 255, 0.4); font-family: var(--kb-font-family); }
  .theme-light { --kb-bg-container: #ffffff; --kb-border-container: #e2e8f0; --kb-shadow-container: 0 20px 25px -5px rgba(0, 0, 0, 0.1); --kb-backdrop-blur: 0px; --kb-text-title: #0f172a; --kb-text-subtitle: #475569; --kb-btn-dismiss-bg: #ffffff; --kb-btn-dismiss-border: #e2e8f0; --kb-btn-dismiss-text: #64748b; --kb-btn-dismiss-hover-bg: #f1f5f9; --kb-btn-dismiss-hover-text: #0f172a; --kb-btn-trigger-bg: #0f172a; --kb-btn-trigger-text: #ffffff; --kb-btn-trigger-hover-bg: #1e293b; --kb-progress-track: #e2e8f0; --kb-progress-indicator: #0f172a; --kb-popover-bg: #ffffff; --kb-popover-border: #e2e8f0; --kb-popover-text: #0f172a; --kb-input-bg: #ffffff; --kb-input-border: #cbd5e1; --kb-input-text: #0f172a; --kb-input-placeholder: #94a3b8; --kb-form-label: #334155; --kb-success-color: #059669; --kb-error-text: #ef4444; --kb-kbd-bg: #f1f5f9; --kb-kbd-border: #e2e8f0; --kb-kbd-text: #64748b; --kb-watermark-text: #94a3b8; }
  .theme-dark { --kb-bg-container: #0f172a; --kb-border-container: #334155; --kb-shadow-container: 0 25px 50px -12px rgba(0, 0, 0, 0.25); --kb-backdrop-blur: 0px; --kb-text-title: #f1f5f9; --kb-text-subtitle: #94a3b8; --kb-btn-dismiss-bg: #1e293b; --kb-btn-dismiss-border: #334155; --kb-btn-dismiss-text: #cbd5e1; --kb-btn-dismiss-hover-bg: #334155; --kb-btn-dismiss-hover-text: #ffffff; --kb-btn-trigger-bg: #ffffff; --kb-btn-trigger-text: #0f172a; --kb-btn-trigger-hover-bg: #f1f5f9; --kb-progress-track: #1e293b; --kb-progress-indicator: #ffffff; --kb-popover-bg: #0f172a; --kb-popover-border: #334155; --kb-popover-text: #f1f5f9; --kb-input-bg: #0f172a; --kb-input-border: #475569; --kb-input-text: #f1f5f9; --kb-input-placeholder: #94a3b8; --kb-form-label: #e2e8f0; --kb-success-color: #6ee7b7; --kb-error-text: #ef4444; --kb-kbd-bg: #1e293b; --kb-kbd-border: #334155; --kb-kbd-text: #94a3b8; --kb-watermark-text: #64748b; }
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  #cue-card-root { position: fixed; z-index: 2147483647; pointer-events: none; width: 100%; height: 0; top: 0; left: 0; isolation: isolate; }
  .container { position: absolute; pointer-events: auto; background-color: var(--kb-bg-container); border: 1px solid var(--kb-border-container); box-shadow: var(--kb-shadow-container); backdrop-filter: blur(var(--kb-backdrop-blur)); -webkit-backdrop-filter: blur(var(--kb-backdrop-blur)); border-radius: 0.25rem; border-bottom-left-radius: 0.25rem; border-bottom-right-radius: 0.25rem; padding: 1.25rem 1.5rem; width: auto; min-width: 400px; max-width: 90vw; opacity: 0; z-index: 1; font-family: var(--kb-font-family); }
  .backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0); backdrop-filter: blur(0); -webkit-backdrop-filter: blur(0); transition: all 0.5s ease; z-index: 0; pointer-events: none; }
  .backdrop.active { background: rgba(0,0,0,0.1); backdrop-filter: blur(var(--kb-focus-blur)); -webkit-backdrop-filter: blur(var(--kb-focus-blur)); }
  .launcher { position: absolute; pointer-events: auto; background-color: var(--kb-bg-container); border: 1px solid var(--kb-border-container); box-shadow: var(--kb-shadow-container); backdrop-filter: blur(var(--kb-backdrop-blur)); -webkit-backdrop-filter: blur(var(--kb-backdrop-blur)); border-radius: 9999px; padding: 0.5rem 1rem; display: flex; align-items: center; gap: 0.75rem; cursor: pointer; transition: transform 0.2s, opacity 0.3s; opacity: 0; z-index: 1; color: var(--kb-text-title); font-weight: 500; font-size: 0.875rem; font-family: var(--kb-font-family); }
  .launcher:hover { transform: scale(1.02); background-color: var(--kb-bg-container); }
  .kb-chip-row { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; }
  .kb-chip { display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.35rem 0.8rem; border-radius: 9999px; border: 1px solid rgba(255,255,255,0.85); background: linear-gradient(135deg, rgba(255,255,255,0.28), rgba(255,255,255,0.08)); color: var(--kb-text-title); font-size: 0.8rem; font-weight: 600; backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); box-shadow: 0 8px 18px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(255,255,255,0.45); }
  .kb-chip-logo { width: 18px; height: 18px; border-radius: 9999px; overflow: hidden; background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; }
  .kb-chip-logo img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .kb-chip-fallback { width: 18px; height: 18px; border-radius: 9999px; display: flex; align-items: center; justify-content: center; font-size: 0.7rem; font-weight: 700; color: var(--kb-text-title); background: rgba(255,255,255,0.25); }
  .kb-chip-action { margin-left: 0.35rem; font-size: 0.7rem; color: var(--kb-text-subtitle); text-decoration: underline; background: none; border: none; padding: 0; cursor: pointer; }
  .kb-chip-action:hover { color: var(--kb-text-title); }
  .kb-chip--launcher { padding: 0.25rem 0.65rem; font-size: 0.7rem; }
  .kb-chip-row.launcher-row { margin-bottom: 0; }
  .launcher-label { display: inline-flex; align-items: center; gap: 0.5rem; }
  .subtitle-chip { margin-top: 0.35rem; }
  .pos-top-center { left: 50%; translate: -50% 0; top: 1rem; }
  .pos-top-right { right: 1rem; top: 1rem; left: auto; }
  @media (max-width: 768px) { .pos-top-center, .pos-top-right { left: 0.5rem; right: 0.5rem; top: 0.5rem; translate: none; width: auto; max-width: none; } }
  :host(.kb-inline) { position: relative !important; z-index: auto !important; top: auto !important; left: auto !important; width: 100% !important; height: auto !important; pointer-events: auto !important; isolation: auto !important; display: block; }
  :host(.kb-inline) .container, :host(.kb-inline) .launcher { position: relative !important; left: auto !important; right: auto !important; top: auto !important; translate: none !important; width: 100% !important; max-width: 100% !important; margin: 0 !important; box-shadow: none !important; }
  :host(.kb-inline) .container { opacity: 1 !important; transform: none !important; }
  :host(.kb-inline) .popover { position: static !important; transform: none !important; opacity: 1 !important; visibility: visible !important; pointer-events: auto !important; margin-top: 0.75rem !important; }
  :host(.kb-inline) .backdrop, :host(.kb-inline) .launcher, :host(.kb-inline) .btn-dismiss { display: none !important; }
  .content-wrapper { display: flex; align-items: center; gap: 1rem; }
  .logo { width: 2.5rem; height: 2.5rem; display: none; }
  @media (min-width: 1024px) { .logo { display: block; } }
  .text-content { flex: 1; user-select: none; }
  .text-row { display: flex; align-items: center; justify-content: space-between; gap: 1.5rem; flex-wrap: wrap; }
  .title { color: var(--kb-text-title); font-size: 1rem; font-weight: 600; line-height: 1.5; }
  .subtitle-text { color: var(--kb-text-title); font-size: 0.875rem; line-height: 1.25; opacity: 0.8; }
  .btn { display: inline-flex; align-items: center; justify-content: center; border-radius: 0.375rem; font-weight: 500; font-size: 0.875rem; line-height: 1.25rem; padding: 0.5rem 1rem; transition: all 0.2s; cursor: pointer; border: none; outline: none; }
  .btn-trigger { background-color: var(--kb-btn-trigger-bg); color: var(--kb-btn-trigger-text); min-width: 8rem; gap: 0.5rem; transition: opacity 0.2s ease-out, background-color 0.2s ease-out; }
  .btn-trigger.hidden { opacity: 0; pointer-events: none; }
  .btn-trigger:not(:disabled):hover { background-color: var(--kb-btn-trigger-hover-bg); }
  .btn-trigger:disabled { opacity: 0.8; cursor: not-allowed; }
  .btn-trigger.success { background-color: transparent; color: var(--kb-success-color); padding-right: 0.75rem; cursor: default; }
  .status-text { display: inline-flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: var(--kb-text-subtitle); opacity: 0.8; }
  .btn-trigger.success:hover { background-color: transparent; }
  .animate-spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes shine { 0% { background-position: 0% 0%; } 50% { background-position: 100% 100%; } 100% { background-position: 0% 0%; } }
  @keyframes sheen-flash { 0% { opacity: 0; transform: translateX(-100%) skewX(-15deg); } 50% { opacity: 0.6; } 100% { opacity: 0; transform: translateX(200%) skewX(-15deg); } }
  .container-sheen { position: absolute; inset: 0; overflow: hidden; pointer-events: none; border-radius: inherit; }
  .container-sheen::after { content: ''; position: absolute; top: 0; left: 0; width: 50%; height: 100%; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent); transform: translateX(-100%) skewX(-15deg); }
  .container-sheen.active::after { animation: sheen-flash 0.6s ease-out forwards; }
  .launcher-shine-border { position: absolute; inset: 0; border-radius: inherit; pointer-events: none; background-size: 300% 300%; animation: shine var(--shine-duration, 14s) linear infinite; mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); mask-composite: exclude; -webkit-mask-composite: xor; padding: var(--shine-border-width, 1px); }
  .btn-dismiss { position: absolute; top: -0.5rem; left: -0.5rem; width: 1.5rem; height: 1.5rem; padding: 0; border-radius: 9999px; display: flex; align-items: center; justify-content: center; background-color: var(--kb-btn-dismiss-bg); border: 1px solid var(--kb-btn-dismiss-border); color: var(--kb-btn-dismiss-text); cursor: pointer; transition: all 0.3s ease-out; z-index: 10; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
  .btn-dismiss:hover { background-color: var(--kb-btn-dismiss-hover-bg); color: var(--kb-btn-dismiss-hover-text); }
  .popover { position: absolute; top: 100%; left: 0; right: 0; margin-top: 0.375rem; background-color: var(--kb-popover-bg); border: 1px solid var(--kb-popover-border); border-radius: 0.25rem; border-top-left-radius: 0; border-top-right-radius: 0; padding: 1rem 1.5rem; color: var(--kb-popover-text); box-shadow: var(--kb-shadow-container); backdrop-filter: blur(var(--kb-backdrop-blur)); -webkit-backdrop-filter: blur(var(--kb-backdrop-blur)); opacity: 0; transform: translateY(-10px); pointer-events: none; visibility: hidden; transition: all 0.2s ease-out; }
  .popover.open { opacity: 1; transform: translateY(0); pointer-events: auto; visibility: visible; }
  .container.popover-open { border-bottom-left-radius: 0; border-bottom-right-radius: 0; }
  .form-group { margin-bottom: 0; }
  .form-label { display: block; margin-bottom: 0.5rem; font-size: 0.875rem; color: var(--kb-form-label); font-weight: 500; }
  .input-row { display: flex; gap: 0.5rem; }
  .form-input { flex: 1; background-color: var(--kb-input-bg); border: 1px solid var(--kb-input-border); color: var(--kb-input-text); border-radius: 0.375rem; padding: 0.5rem 0.75rem; font-size: 0.875rem; outline: none; transition: border-color 0.2s; min-height: 44px; }
  .form-input::placeholder { color: var(--kb-input-placeholder); }
  .form-input:focus { border-color: var(--kb-progress-indicator); }
  .form-input.error { border-color: var(--kb-error-text); }
  .form-label.error { color: var(--kb-error-text); }
  .form-message { color: var(--kb-error-text); font-size: 0.75rem; margin-top: 0.25rem; font-weight: 500; }
  .btn-submit { background-color: var(--kb-btn-trigger-bg); color: var(--kb-btn-trigger-text); padding: 0.5rem 1rem; display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem; }
  .btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }
  .kbd-hint { display: flex; align-items: center; gap: 0.5rem; position: absolute; top: calc(100% + 0.5rem); left: 50%; transform: translateX(-50%); opacity: 0; pointer-events: none; transition: opacity 0.2s ease; white-space: nowrap; }
  .kbd-hint.visible { opacity: 1; }
  .kbd-group { display: flex; align-items: center; gap: 0.25rem; }
  .kbd { font-family: inherit; font-size: 0.75rem; line-height: 1; padding: 0.25rem 0.375rem; border-radius: 0.25rem; background: var(--kb-kbd-bg); border: 1px solid var(--kb-kbd-border); color: var(--kb-kbd-text); font-weight: 500; min-width: 1.25rem; text-align: center; }
  .kbd-text { font-size: 0.75rem; color: var(--kb-kbd-text); }
  .watermark { text-align: center; font-size: 0.625rem; color: var(--kb-watermark-text); opacity: 0.8; font-weight: 500; letter-spacing: 0.025em; }
  .watermark a { color: inherit; text-decoration: none; cursor: pointer; }
  .watermark a:hover { text-decoration: underline; text-decoration-color: var(--kb-watermark-text); }
  .popover-watermark { position: absolute; top: 100%; left: 0; right: 0; text-align: center; font-size: 0.625rem; color: var(--kb-watermark-text); opacity: 0.6; font-weight: 500; letter-spacing: 0.025em; padding-top: 0.5rem; }
  .popover-watermark a { color: inherit; text-decoration: none; cursor: pointer; }
  .popover-watermark a:hover { text-decoration: underline; opacity: 1; }
  /* Launcher helper hint (below launcher) */
  .launcher-hint { position: absolute; top: calc(100% + 0.4rem); left: 50%; transform: translateX(-50%); pointer-events: none; white-space: nowrap; }
  .launcher-hint.visible { opacity: 0.65; }
  /* Launcher dismiss button */
  .launcher .btn-dismiss.launcher-dismiss { top: -0.375rem; left: -0.375rem; width: 1.25rem; height: 1.25rem; opacity: 0; transition: opacity 0.2s ease-out, background-color 0.3s ease-out, color 0.3s ease-out; }
  .launcher:hover .btn-dismiss.launcher-dismiss { opacity: 1; }
  /* Launcher logo */
  .launcher-logo { width: 20px; height: 20px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
  .launcher-logo svg { width: 20px; height: 20px; }
  /* Mobile: show dismiss button */
  @media (max-width: 768px) {
    .launcher .btn-dismiss.launcher-dismiss { opacity: 1; }
  }
  /* Toggle personalization button */
  .btn-toggle-personalization { position: relative; width: 1.75rem; height: 1.75rem; padding: 0; border-radius: 9999px; display: flex; align-items: center; justify-content: center; background-color: var(--kb-btn-dismiss-bg); border: 1px solid var(--kb-btn-dismiss-border); color: var(--kb-btn-dismiss-text); cursor: pointer; transition: all 0.2s ease-out; flex-shrink: 0; margin-left: 0.25rem; }
  .btn-toggle-personalization:hover { background-color: var(--kb-btn-dismiss-hover-bg); color: var(--kb-btn-dismiss-hover-text); transform: scale(1.05); }
  .btn-toggle-personalization svg { width: 14px; height: 14px; }
  /* Custom tooltip for toggle button */
  .btn-toggle-personalization .toggle-tooltip { position: absolute; top: calc(100% + 6px); left: 50%; transform: translateX(-50%); white-space: nowrap; background-color: var(--kb-bg-container); border: 1px solid var(--kb-border-container); color: var(--kb-text-title); font-size: 0.7rem; font-weight: 500; padding: 0.25rem 0.5rem; border-radius: 4px; opacity: 0; visibility: hidden; transition: opacity 0.15s ease-out, visibility 0.15s ease-out; pointer-events: none; z-index: 100; box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
  .btn-toggle-personalization:hover .toggle-tooltip { opacity: 1; visibility: visible; }
  .btn-toggle-personalization.loading { cursor: default; opacity: 0.7; }
  .btn-toggle-personalization.loading:hover { transform: none; }
  .btn-toggle-personalization:disabled { cursor: default; }
  .launcher-domain { text-decoration: underline; text-underline-offset: 2px; }
  .launcher-actions { display: flex; align-items: center; gap: 0.25rem; }
`;

// -- Animation Hook --
const useEnterExitAnimation = (
  ref: preact.RefObject<HTMLElement>,
  isVisible: boolean,
  config: {
    direction: CueCardDirection;
    position: CueCardPosition;
    entranceDelayMs: number;
    exitDelayMs: number;
    onEntranceComplete?: () => void;
    onExitComplete?: () => void;
  }
) => {
  const hasEnteredRef = useRef(false);
  const currentAnimRef = useRef<Animation | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Cancel any running animation to prevent overlaps
    if (currentAnimRef.current) {
      currentAnimRef.current.cancel();
      currentAnimRef.current = null;
    }

    const isTopDown = config.direction === "top-to-bottom";
    const initialTransform = isTopDown
      ? `translateY(-60px)`
      : `translateX(60px)`;
    const exitTransform = isTopDown ? `translateY(-20px)` : `translateX(20px)`;
    const finalTransform = "translate(0, 0)";

    if (isVisible) {
      // Show element before entrance animation
      element.style.visibility = "visible";
      hasEnteredRef.current = true;

      const anim = element.animate(
        [
          { opacity: 0, transform: initialTransform },
          { opacity: 1, transform: finalTransform },
        ],
        {
          duration: 500,
          delay: config.entranceDelayMs,
          easing: "cubic-bezier(0.175, 0.885, 0.32, 1.275)",
          fill: "forwards",
        }
      );
      currentAnimRef.current = anim;
      anim.onfinish = () => {
        currentAnimRef.current = null;
        config.onEntranceComplete?.();
      };
    } else {
      // Exit
      if (hasEnteredRef.current) {
        const anim = element.animate(
          [
            { opacity: 1, transform: finalTransform },
            { opacity: 0, transform: exitTransform },
          ],
          {
            duration: 350,
            delay: config.exitDelayMs,
            easing: "ease-in-out",
            fill: "forwards",
          }
        );
        currentAnimRef.current = anim;
        anim.onfinish = () => {
          // Hide element after exit animation completes
          element.style.visibility = "hidden";
          currentAnimRef.current = null;
          config.onExitComplete?.();
        };
      } else {
        // Never entered, ensure it's hidden immediately
        element.style.opacity = "0";
        element.style.transform = exitTransform;
        element.style.visibility = "hidden";
      }
    }
  }, [isVisible, config.direction]);
};

// -- Helper to map custom theme to CSS variables --
const getThemeStyles = (theme?: CueCardThemeConfig) => {
  if (!theme) return {};
  const styles: Record<string, string | undefined> = {};

  if (theme.fontFamily) styles["--kb-font-family"] = theme.fontFamily;
  if (theme.backgroundColor) {
    styles["--kb-bg-container"] = theme.backgroundColor;
    styles["--kb-popover-bg"] = theme.backgroundColor;
  }
  if (theme.borderColor) {
    styles["--kb-border-container"] = theme.borderColor;
    styles["--kb-popover-border"] = theme.borderColor;
  }
  if (theme.textColor) {
    styles["--kb-text-title"] = theme.textColor;
    styles["--kb-popover-text"] = theme.textColor;
  }
  if (theme.subtitleColor) styles["--kb-text-subtitle"] = theme.subtitleColor;
  if (theme.borderRadius) {
    styles["border-radius"] = theme.borderRadius;
    styles["border-bottom-left-radius"] = theme.borderRadius;
    styles["border-bottom-right-radius"] = theme.borderRadius;
  }

  // Button & Accents
  if (theme.primaryColor) {
    styles["--kb-btn-trigger-bg"] = theme.primaryColor;
    styles["--kb-progress-indicator"] = theme.primaryColor;
    styles["--kb-success-color"] = theme.primaryColor;
  }
  if (theme.primaryTextColor) {
    styles["--kb-btn-trigger-text"] = theme.primaryTextColor;
  }

  return styles as any; // Preact style types can be strict
};

// -- Components --

const CueCardContent: FunctionalComponent<{
  config: CueCardConfig;
  isOpen: boolean;
  skipFocus?: boolean;
  setIsOpen: (val: boolean) => void;
  deanonymizedCompany?: DeanonymizedCompany | null;
  onClearDeanonymizedCompany?: () => void;
}> = ({
  config,
  isOpen,
  skipFocus,
  setIsOpen,
  deanonymizedCompany,
  onClearDeanonymizedCompany,
}) => {
  const isInline = config.mountMode === "inline";
  const containerRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const [mode, setMode] = useState<"card" | "launcher">("card");
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasHadSuccess, setHasHadSuccess] = useState(false);
  const [isExitingToLauncher, setIsExitingToLauncher] = useState(false);
  const hasEnteredRef = useRef(false);
  const isShowingInitialSuccessRef = useRef(false);
  const justReopenedRef = useRef(false);
  const previousModeRef = useRef<"card" | "launcher">("card");
  const prevStatusRef = useRef(config.status); // Add this
  const prevVisibilityRef = useRef(!!config.isVisible);
  const timerStartRef = useRef<number | null>(null);
  const timerRafRef = useRef<number | null>(null);
  const hasTimerCompletedRef = useRef(false);
  const [isHovering, setIsHovering] = useState(false);
  const [sheenActive, setSheenActive] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [timerProgress, setTimerProgressState] = useState(0);
  const timerProgressRef = useRef(0);
  const timerEnabled = config.enableTimer !== false;
  const timerDuration = Math.max(500, config.timerDurationMs ?? 20_000);
  const isPending = config.status === "starting";
  const isSuccess = config.status === "success";
  const isError = config.status === "error";
  const isDeanonymized = !!deanonymizedCompany;
  const isPopoverEnabled = !isDeanonymized;
  const isPopoverOpen = isPopoverEnabled ? isOpen : false;
  const deanonymizedLabel =
    deanonymizedCompany?.name || deanonymizedCompany?.domain || "";
  const deanonymizedDomain = deanonymizedCompany?.domain;

  const setTimerProgress = useCallback((value: number) => {
    timerProgressRef.current = value;
    setTimerProgressState(value);
  }, []);

  useEffect(() => {
    if (config.isVisible) {
      setMode("card");
      hasEnteredRef.current = false;
      setIsDismissed(false);
    }
  }, [config.isVisible]);

  // -- Render-time State Logic --

  // 1. Detect re-opening (Launcher -> Card) and clear initial success flag
  // This ensures that when we re-open, we don't show the old success state
  if (mode === "card" && previousModeRef.current === "launcher") {
    isShowingInitialSuccessRef.current = false;
  }

  // 2. Handle success transition immediately during render to ensure no flash of wrong state
  if (config.status === "success" && prevStatusRef.current !== "success") {
    // If we just transitioned to success and we're in card mode, show the success state
    if (mode === "card") {
      isShowingInitialSuccessRef.current = true;
    }
  } else if (config.status !== "success") {
    isShowingInitialSuccessRef.current = false;
  }
  // Update tracking ref
  prevStatusRef.current = config.status;

  // Track mode transitions to detect reopening
  useEffect(() => {
    const wasLauncher = previousModeRef.current === "launcher";
    const isCard = mode === "card";

    // If we transitioned from launcher to card after success, mark as reopening
    if (wasLauncher && isCard && hasHadSuccess) {
      justReopenedRef.current = true;
      setTimeout(() => {
        justReopenedRef.current = false;
      }, 100);
    }

    if (mode === "launcher") {
      setIsHovering(false);
    }

    if (wasLauncher && isCard && timerEnabled) {
      setTimerProgress(0);
      timerStartRef.current = null;
      hasTimerCompletedRef.current = false;
    }
  }, [mode, setTimerProgress, timerEnabled]);

  // Track success state and auto-dismiss to launcher
  useEffect(() => {
    if (config.status === "success") {
      setHasHadSuccess(true);

      // Auto-dismiss to launcher after a short delay if launcher is enabled
      // Only if we are currently showing the initial success state
      if (
        config.enableLauncher &&
        mode === "card" &&
        isShowingInitialSuccessRef.current
      ) {
        const timer = setTimeout(() => {
          setMode("launcher");
          // Don't clear isShowingInitialSuccessRef here, let it persist during exit animation
          // We clear it in the render-time logic when switching back to card mode
        }, 2000); // 2 second delay after success

        return () => clearTimeout(timer);
      }
    }
  }, [config.status, config.enableLauncher, mode]);

  // Reset the idle timer when the card becomes visible again
  useEffect(() => {
    if (config.isVisible && !prevVisibilityRef.current && timerEnabled) {
      setTimerProgress(0);
      timerStartRef.current = null;
      hasTimerCompletedRef.current = false;
    }
    prevVisibilityRef.current = !!config.isVisible;
  }, [config.isVisible, setTimerProgress, timerEnabled]);

  // Update previous mode ref (runs after other effects)
  useEffect(() => {
    previousModeRef.current = mode;
  }, [mode]);

  // Store skipFocus in a ref so it persists for the effect
  const skipFocusRef = useRef(false);
  if (skipFocus) {
    skipFocusRef.current = true;
  }

  useEffect(() => {
    if (isPopoverOpen) {
      // Reset form if opening after success (preparing for "Show me again")
      if (hasHadSuccess && formRef.current) {
        formRef.current.reset();
      }

      // Focus the first input when the popover opens (unless skipFocus is set)
      // Use a small delay to ensure the popover is visible (CSS transition is 200ms)
      const shouldSkipFocus = skipFocusRef.current;
      skipFocusRef.current = false; // Reset after reading

      if (!shouldSkipFocus) {
        const focusTimer = setTimeout(() => {
          if (firstInputRef.current) {
            firstInputRef.current.focus();
          }
        }, 100);

        return () => clearTimeout(focusTimer);
      }
    }
  }, [isPopoverOpen]);

  // Track previous isOpen state to detect transitions
  const prevIsOpenForExpandRef = useRef(isPopoverOpen);
  useEffect(() => {
    const wasOpen = prevIsOpenForExpandRef.current;
    prevIsOpenForExpandRef.current = isPopoverOpen;

    // Only expand from launcher to card when isOpen changes from false to true
    if (!wasOpen && isPopoverOpen && mode === "launcher") {
      setMode("card");
    }
  }, [isPopoverOpen, mode]);

  // Track if we're transitioning between modes (need delay for crossfade)
  const isFirstAppearance = !hasEnteredRef.current;
  const wasLauncherPreviously = previousModeRef.current === "launcher";

  // Card entrance delay: use config delay on first appearance, or wait for launcher exit when reopening
  const cardEntranceDelay = isFirstAppearance
    ? config.entranceDelayMs || 0
    : wasLauncherPreviously && mode === "card"
    ? 400 // Wait for launcher exit
    : 0;

  if (!isInline) {
    useEnterExitAnimation(
      containerRef,
      !!config.isVisible && mode === "card" && !isDismissed,
      {
        direction: config.direction || "top-to-bottom",
        position: config.position || "top-center",
        entranceDelayMs: cardEntranceDelay,
        exitDelayMs: 75,
        onEntranceComplete: () => {
          config.onEntranceComplete?.();
          hasEnteredRef.current = true;
        },
        onExitComplete: () => {
          if (!config.isVisible || isDismissed) config.onExitComplete?.();
        },
      }
    );

    // Delay launcher entrance to allow card exit animation to complete
    // Card exit: 75ms delay + 350ms duration = 425ms total
    const launcherEntranceDelay = 400;

    useEnterExitAnimation(
      launcherRef,
      !!config.isVisible && mode === "launcher" && !isDismissed,
      {
        direction: config.direction || "top-to-bottom",
        position: config.position || "top-center",
        entranceDelayMs: launcherEntranceDelay,
        exitDelayMs: 75,
      }
    );
  }

  useEffect(() => {
    const el = backdropRef.current;
    if (!el) return;

    // Check if status is success AND we're showing the initial success state
    // When reopening from launcher, isShowingInitialSuccessRef is false so blur should apply
    const isShowingSuccessState =
      config.status === "success" && isShowingInitialSuccessRef.current;

    if (
      config.enableFocusMode &&
      mode === "card" &&
      config.isVisible &&
      !isShowingSuccessState
    ) {
      const timer = setTimeout(() => {
        el.classList.add("active");
      }, config.focusDelayMs || 500);
      return () => clearTimeout(timer);
    } else {
      el.classList.remove("active");
    }
  }, [
    config.enableFocusMode,
    mode,
    config.isVisible,
    config.focusDelayMs,
    config.status,
  ]);

  useEffect(() => {
    if (!config.isVisible) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // ESC always goes directly to launcher (or dismisses if no launcher)
        if (mode === "card") {
          // Don't close form first - go straight to launcher to avoid button flash
          if (config.enableLauncher) {
            setIsExitingToLauncher(true);
            setMode("launcher");
            // Reset isOpen after card has fully exited
            setTimeout(() => {
              setIsOpen(false);
              setIsExitingToLauncher(false);
            }, 500);
          } else {
            setIsOpen(false);
            config.onDismiss?.();
          }
        }
        return;
      }

      // Helper to check if configured shortcut matches event
      const matchesShortcut = (
        e: KeyboardEvent,
        shortcut?: CueCardKeyboardShortcut
      ) => {
        const s = shortcut || { key: "p", modifiers: ["meta", "ctrl"] }; // Default: Cmd+P or Ctrl+P

        // Check Key
        if (e.key.toLowerCase() !== s.key.toLowerCase()) return false;

        // Check Modifiers
        const mods = s.modifiers || [];
        // For default (Cmd OR Ctrl), we check if either is present if both are listed in our 'default' logic above
        // But for strict config, we usually want ALL specified modifiers to be pressed.

        // If custom shortcut provided:
        if (shortcut) {
          const hasMeta = mods.includes("meta") === e.metaKey;
          const hasCtrl = mods.includes("ctrl") === e.ctrlKey;
          const hasAlt = mods.includes("alt") === e.altKey;
          const hasShift = mods.includes("shift") === e.shiftKey;
          return hasMeta && hasCtrl && hasAlt && hasShift;
        }

        // Default fallback logic (Cmd OR Ctrl + P)
        // We treat 'meta' or 'ctrl' as "Command or Control" loosely for the default
        const isCmdOrCtrl = e.metaKey || e.ctrlKey;
        return isCmdOrCtrl;
      };

      if (matchesShortcut(e, config.keyboardShortcut)) {
        e.preventDefault();
        if (mode === "launcher") {
          setMode("card");
          setIsOpen(isPopoverEnabled);
        } else if (mode === "card") {
          if (config.enableLauncher) {
            setMode("launcher");
          } else {
            config.onDismiss?.();
          }
        }
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [
    config.isVisible,
    isPopoverOpen,
    mode,
    config.enableLauncher,
    setIsOpen,
    config.onDismiss,
    isPopoverEnabled,
  ]);

  const [isMobile, setIsMobile] = useState(false);
  const isMac =
    typeof navigator !== "undefined" && /Mac/.test(navigator.userAgent);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(media.matches);
    update();
    if ("addEventListener" in media) {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const launcherHint = (() => {
    if (isMobile) {
      return { keys: [] as string[], label: "tap to launch" };
    }

    if (config.keyboardShortcut) {
      const mods = config.keyboardShortcut.modifiers || [];
      const keys: string[] = [];
      if (mods.includes("meta")) keys.push(isMac ? "⌘" : "Ctrl");
      if (mods.includes("ctrl")) keys.push("Ctrl");
      if (mods.includes("alt")) keys.push(isMac ? "Opt" : "Alt");
      if (mods.includes("shift")) keys.push("Shift");
      keys.push(config.keyboardShortcut.key.toUpperCase());
      return { keys, label: "to launch" };
    }

    return { keys: [isMac ? "⌘" : "Ctrl", "P"], label: "to launch" };
  })();

  const [logoFailed, setLogoFailed] = useState(false);
  const [logoAttempt, setLogoAttempt] = useState<"primary" | "fallback">(
    "primary"
  );

  useEffect(() => {
    setLogoFailed(false);
    setLogoAttempt("primary");
  }, [deanonymizedCompany?.logoUrl, deanonymizedLabel, deanonymizedDomain]);

  useEffect(() => {
    if (!isPopoverEnabled && isOpen) {
      setIsOpen(false);
    }
  }, [isPopoverEnabled, isOpen, setIsOpen]);

  const logoUrlTemplate = config.deanonymization?.logoUrlTemplate;
  const normalizedLogoDomain = normalizeDomain(deanonymizedDomain);
  const fallbackLogoUrl = normalizedLogoDomain
    ? `https://logo.clearbit.com/${normalizedLogoDomain}`
    : undefined;
  const primaryLogoUrl =
    normalizedLogoDomain && logoUrlTemplate
      ? logoUrlTemplate.replace("{domain}", normalizedLogoDomain)
      : deanonymizedCompany?.logoUrl || fallbackLogoUrl;
  const resolvedLogoUrl =
    logoAttempt === "fallback" &&
    fallbackLogoUrl &&
    fallbackLogoUrl !== primaryLogoUrl
      ? fallbackLogoUrl
      : primaryLogoUrl;

  // Only show success styling during the initial success phase, not when re-opening
  const showAsSuccess = isSuccess && isShowingInitialSuccessRef.current;

  const txt = config.textOverrides || {};

  const titleText = (() => {
    if (isSuccess) return txt.titleSuccess || "Personalization complete";
    if (isPending) return "Starting personalization"; // usually transitional, rarely customized
    if (isError) return txt.titleError || "Let's try that again";
    return config.hasPersonalization
      ? "Personalization ready"
      : txt.titleResting || "Personalization available";
  })();

  const subtitleText = (() => {
    if (isSuccess) return txt.subtitleSuccess || "Take a look";
    if (isPending) return "Hang tight...";
    if (isError)
      return txt.subtitleError || "Something went wrong. Tap to retry.";
    return txt.subtitleResting || "This page can adapt to you";
  })();

  const buttonText = (() => {
    if (isPending) return txt.btnLoading || "Starting...";
    if (isError) return txt.btnError || "Try again";

    // If currently showing initial success state, show "Personalised"
    if (isSuccess && isShowingInitialSuccessRef.current) {
      return txt.btnSuccess || "Personalised";
    }

    // If we've had success at all, show "Show me again"
    if (hasHadSuccess) {
      return "Show me again";
    }

    // Initial state: show "Show me"
    return txt.btnResting || "Show me";
  })();

  const launcherLabelText = (() => {
    if (isDeanonymized) {
      if (hasHadSuccess) {
        return (
          txt.launcherLabelDeanonymizedSuccess ||
          txt.launcherLabelDeanonymized ||
          "See it again for"
        );
      }
      return txt.launcherLabelDeanonymized || "See it in action for";
    }
    if (hasHadSuccess) {
      return (
        txt.launcherLabelSuccess || txt.launcherLabel || "Personalize again"
      );
    }
    return txt.launcherLabel || "Personalize";
  })();

  const renderDeanonymizedChip = (variant: "card" | "launcher") => {
    if (!deanonymizedCompany || !deanonymizedLabel) return null;
    const isLauncher = variant === "launcher";
    const fallbackLetter = deanonymizedLabel.trim().slice(0, 1).toUpperCase();
    const showLogo = !!resolvedLogoUrl && !logoFailed;
    return (
      <div class={`kb-chip ${isLauncher ? "kb-chip--launcher" : ""}`}>
        {showLogo ? (
          <span class="kb-chip-logo">
            <img
              src={resolvedLogoUrl}
              alt=""
              referrerPolicy="no-referrer"
              onError={() => {
                if (
                  logoAttempt === "primary" &&
                  fallbackLogoUrl &&
                  fallbackLogoUrl !== primaryLogoUrl
                ) {
                  setLogoAttempt("fallback");
                  return;
                }
                setLogoFailed(true);
              }}
            />
          </span>
        ) : (
          <span class="kb-chip-fallback">{fallbackLetter}</span>
        )}
        <span>{deanonymizedLabel}</span>
        {onClearDeanonymizedCompany && (
          <button
            class="kb-chip-action"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClearDeanonymizedCompany();
              setIsOpen(true);
            }}
          >
            Not you?
          </button>
        )}
      </div>
    );
  };

  const buildDeanonymizedValues = () => {
    const normalizedInputDomain = normalizeDomain(deanonymizedDomain);
    const fallbackValue = (
      normalizedInputDomain ||
      deanonymizedDomain ||
      deanonymizedLabel ||
      ""
    ).toString();
    const values: PersonalizationInput = {};

    config.fields.forEach((field) => {
      const key = field.name;
      const lowerKey = key.toLowerCase();
      if (lowerKey.includes("domain")) {
        values[key] = normalizedInputDomain || fallbackValue;
        return;
      }
      if (lowerKey.includes("company")) {
        values[key] = deanonymizedLabel;
        return;
      }
      values[key] = fallbackValue;
    });

    return values;
  };

  const submitValues = (values: PersonalizationInput) => {
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    config.fields.forEach((field) => {
      const rawValue = values[field.name] ?? "";
      const value = typeof rawValue === "string" ? rawValue : String(rawValue);
      const error = validateField(field.name, value, field);
      if (error) {
        newErrors[field.name] = error;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setIsOpen(false);
    config.onSubmitPersonalization?.(values);
  };

  const validateField = (
    name: string,
    value: string,
    fieldConfig?: PersonalizationField
  ): string | null => {
    if (!fieldConfig) return null;

    // Required check
    if (fieldConfig.required && !value.trim()) {
      return fieldConfig.errorMessage || `${fieldConfig.label} is required`;
    }

    if (!value) return null; // Skip other checks if empty and not required

    // Email check
    if (fieldConfig.type === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return fieldConfig.errorMessage || "Please enter a valid email address";
      }
    }

    // URL check
    if (fieldConfig.type === "url") {
      try {
        new URL(value);
      } catch {
        return (
          fieldConfig.errorMessage ||
          "Please enter a valid URL (e.g., https://example.com)"
        );
      }
    }

    // Min length
    if (fieldConfig.minLength && value.length < fieldConfig.minLength) {
      return (
        fieldConfig.errorMessage ||
        `Must be at least ${fieldConfig.minLength} characters`
      );
    }

    // Max length
    if (fieldConfig.maxLength && value.length > fieldConfig.maxLength) {
      return (
        fieldConfig.errorMessage ||
        `Must be at most ${fieldConfig.maxLength} characters`
      );
    }

    // Pattern check
    if (fieldConfig.pattern) {
      const regex = new RegExp(fieldConfig.pattern);
      if (!regex.test(value)) {
        return fieldConfig.errorMessage || "Invalid format";
      }
    }

    return null;
  };

  const handleSubmit = (e: Event) => {
    e.preventDefault();
    if (isPending) return;

    const formData = new FormData(e.target as HTMLFormElement);
    const values: PersonalizationInput = {};

    formData.forEach((value, key) => {
      const strValue = value.toString();
      values[key] = strValue;
    });

    submitValues(values);
  };

  const handleInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const name = target.name;

    // If there's an existing error for this field, re-validate on input
    if (errors[name]) {
      const fieldConfig = config.fields.find((f) => f.name === name);
      const error = validateField(name, target.value, fieldConfig);

      setErrors((prev) => {
        const next = { ...prev };
        if (error) {
          next[name] = error;
        } else {
          delete next[name];
        }
        return next;
      });
    }
  };

  const handleDeanonymizedSubmit = () => {
    if (isPending || !isDeanonymized) return;
    submitValues(buildDeanonymizedValues());
  };

  const togglePopover = (e: Event) => {
    e.stopPropagation();
    if (isDeanonymized) return;
    if (isPending || isSuccess) return;
    const opening = !isPopoverOpen;
    setIsOpen(opening);

    // Trigger sheen effect when opening
    if (opening && config.enableClickSheen) {
      setSheenActive(true);
      setTimeout(() => setSheenActive(false), 600);
    }
  };

  // Timer-driven progress bar for first load
  useEffect(() => {
    if (!timerEnabled) return;
    if (hasTimerCompletedRef.current) return;

    const shouldRun =
      config.isVisible &&
      mode === "card" &&
      config.status === "resting" &&
      !isPopoverOpen &&
      !isHovering;

    if (!shouldRun) {
      if (timerRafRef.current !== null) {
        cancelAnimationFrame(timerRafRef.current);
        timerRafRef.current = null;
      }
      timerStartRef.current = null;
      return;
    }

    const tick = (timestamp: number) => {
      if (timerStartRef.current === null) {
        timerStartRef.current =
          timestamp - (timerProgressRef.current / 100) * timerDuration;
      }

      const elapsed = timestamp - timerStartRef.current;
      const pct = Math.min(1, elapsed / timerDuration);
      const nextProgress = pct * 100;
      setTimerProgress(nextProgress);

      if (pct >= 1) {
        hasTimerCompletedRef.current = true;
        timerStartRef.current = null;
        timerRafRef.current = null;
        if (config.enableLauncher) {
          setMode("launcher");
          setIsOpen(false);
        } else {
          config.onDismiss?.();
        }
        return;
      }

      timerRafRef.current = requestAnimationFrame(tick);
    };

    timerRafRef.current = requestAnimationFrame(tick);

    return () => {
      if (timerRafRef.current !== null) {
        cancelAnimationFrame(timerRafRef.current);
        timerRafRef.current = null;
      }
    };
  }, [
    config.enableLauncher,
    config.isVisible,
    config.onDismiss,
    config.status,
    isHovering,
    isPopoverOpen,
    mode,
    setIsOpen,
    timerDuration,
    timerEnabled,
    setTimerProgress,
  ]);

  useEffect(() => {
    return () => {
      if (timerRafRef.current !== null) {
        cancelAnimationFrame(timerRafRef.current);
      }
    };
  }, []);

  const clampedProgress = Math.max(0, Math.min(100, config.progressPct ?? 0));
  const displayedProgress = timerEnabled ? timerProgress : clampedProgress;

  // Compute visibility flags for pointer-events (same conditions as animations)
  const isContainerVisible =
    !!config.isVisible && mode === "card" && !isDismissed;
  const isLauncherVisible =
    !!config.isVisible && mode === "launcher" && !isDismissed;
  const isAnythingVisible = isContainerVisible || isLauncherVisible;
  const showLauncherHint = !!config.showKeyboardHints && isLauncherVisible;

  return (
    <Fragment>
      <div
        ref={backdropRef}
        class="backdrop"
        style={{ display: isAnythingVisible ? "block" : "none" }}
      />
      <div
        ref={containerRef}
        class={`container theme-${config.theme || "glass"} pos-${
          config.position || "top-center"
        }${isPopoverOpen ? " popover-open" : ""}`}
        style={{
          ...getThemeStyles(config.customTheme),
          pointerEvents: isContainerVisible ? "auto" : "none",
        }}
        onClick={() =>
          !isPopoverOpen &&
          !isPending &&
          !isSuccess &&
          isPopoverEnabled &&
          setIsOpen(true)
        }
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {config.enableClickSheen && (
          <div class={`container-sheen${sheenActive ? " active" : ""}`} />
        )}
        <button
          class="btn-dismiss"
          aria-label="Dismiss"
          onClick={(e) => {
            e.stopPropagation();
            hasTimerCompletedRef.current = true;
            if (config.enableLauncher) {
              setMode("launcher");
            } else {
              config.onDismiss?.();
            }
          }}
        >
          <XMarkIcon />
        </button>

        <div class="content-wrapper">
          {/* <div class="logo">
            <LogoIcon />
          </div> */}

          <div class="text-content">
            <div class="text-row">
              <div>
                <div class="title">{titleText}</div>
                {isDeanonymized ? (
                  <div class="subtitle-chip">
                    {renderDeanonymizedChip("card")}
                  </div>
                ) : (
                  <div class="subtitle-text">{subtitleText}</div>
                )}
              </div>

              {isPending ? (
                <span class="status-text">
                  <LoaderIcon /> {buttonText}
                </span>
              ) : (
                <button
                  class={`btn btn-trigger ${showAsSuccess ? "success" : ""}${
                    isPopoverOpen || isExitingToLauncher ? " hidden" : ""
                  }`}
                  disabled={showAsSuccess}
                  onClick={
                    isDeanonymized ? handleDeanonymizedSubmit : togglePopover
                  }
                >
                  {showAsSuccess ? (
                    <Fragment>
                      <CheckIcon /> {buttonText}
                    </Fragment>
                  ) : (
                    buttonText
                  )}
                </button>
              )}
            </div>
          </div>
        </div>


        {!isDeanonymized && (
          <div
            class={`popover ${isPopoverOpen ? "open" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <form onSubmit={handleSubmit} ref={formRef}>
              {config.fields.map((field, idx) => (
                <div class="form-group" key={field.name}>
                  <label
                    class={`form-label ${errors[field.name] ? "error" : ""}`}
                  >
                    {field.label}
                  </label>
                  <div class="input-row">
                    <input
                      ref={idx === 0 ? firstInputRef : undefined}
                      class={`form-input ${errors[field.name] ? "error" : ""}`}
                      name={field.name}
                      type={field.type || "text"}
                      placeholder={field.placeholder}
                      required={field.required}
                      onInput={handleInput}
                      // Auto-focus logic could be added here with another useEffect/ref
                    />
                    {config.fields.length === 1 && (
                      <button
                        type="submit"
                        class="btn btn-submit"
                        disabled={isPending}
                      >
                        {isPending ? <LoaderIcon /> : <ArrowRightIcon />}
                      </button>
                    )}
                  </div>
                  {errors[field.name] && (
                    <div class="form-message">{errors[field.name]}</div>
                  )}
                </div>
              ))}
              {config.fields.length > 1 && (
                <button
                  type="submit"
                  class="btn btn-submit"
                  style={{ width: "100%" }}
                  disabled={isPending}
                >
                  {isPending ? <LoaderIcon /> : <ArrowRightIcon />}
                </button>
              )}
            </form>
            {config.showWatermark && (
              <div class="popover-watermark">
                Powered by{" "}
                <a
                  href="https://kenobi.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Kenobi
                </a>
              </div>
            )}
          </div>
        )}

        {config.showKeyboardHints && config.enableFocusMode && (
          <div
            class={`kbd-hint ${
              !isPopoverOpen && !isPending && !isSuccess ? "visible" : ""
            }`}
          >
            <div class="kbd-group">
              <div class="kbd">Esc</div>
            </div>
            <span class="kbd-text">to dismiss</span>
          </div>
        )}
      </div>

      <div
        ref={launcherRef}
        class={`launcher theme-${config.theme || "glass"} pos-${
          config.position || "top-center"
        }`}
        style={{
          ...getThemeStyles(config.customTheme),
          ...config.launcherStyles,
          pointerEvents: isLauncherVisible ? "auto" : "none",
        }}
        onClick={(e) => {
          e.stopPropagation();
          setIsDismissed(false);
          setMode("card");
          setIsOpen(isPopoverEnabled);
        }}
        role="button"
        tabIndex={0}
      >
        {config.launcherShineBorder?.enabled && (
          <div
            class="launcher-shine-border"
            style={{
              "--shine-duration": `${
                config.launcherShineBorder.duration || 14
              }s`,
              "--shine-border-width": `${
                config.launcherShineBorder.borderWidth || 1
              }px`,
              backgroundImage: `radial-gradient(transparent, transparent, ${
                Array.isArray(config.launcherShineBorder.color)
                  ? config.launcherShineBorder.color.join(",")
                  : config.launcherShineBorder.color || "rgba(255,255,255,0.5)"
              }, transparent, transparent)`,
            }}
          />
        )}
        <button
          class="btn-dismiss launcher-dismiss"
          aria-label="Dismiss"
          onClick={(e) => {
            e.stopPropagation();
            setIsDismissed(true);
            config.onDismiss?.();
          }}
        >
          <XMarkIcon />
        </button>
        {config.showLauncherLogo !== false && (
          <div class="launcher-logo">
            <LogoIcon />
          </div>
        )}
        <div class="launcher-label">
          <span>
            {(() => {
              // Parse "For {domain}" pattern to underline the domain
              const forMatch = launcherLabelText.match(/^(For\s+)(.+)$/i);
              if (forMatch) {
                return (
                  <Fragment>
                    {forMatch[1]}
                    <span class="launcher-domain">{forMatch[2]}</span>
                  </Fragment>
                );
              }
              return launcherLabelText;
            })()}
          </span>
          {isDeanonymized && renderDeanonymizedChip("launcher")}
        </div>

        <div class="launcher-actions">
          {config.enableUndoToggle && hasHadSuccess && (
            <button
              class="btn-toggle-personalization"
              aria-label={
                config.isPersonalized ? "Show original" : "Show personalized"
              }
              onClick={(e) => {
                e.stopPropagation();
                config.onTogglePersonalization?.();
              }}
            >
              <ArrowPathIcon />
              <span class="toggle-tooltip">
                {config.isPersonalized ? "Show original" : "Show personalized"}
              </span>
            </button>
          )}

          {config.launcherAction &&
            config.launcherAction.status !== "idle" &&
            hasHadSuccess && (
              <button
                class={`btn-toggle-personalization${
                  config.launcherAction.status === "loading" ? " loading" : ""
                }`}
                aria-label={config.launcherAction.tooltip || "Action"}
                onClick={(e) => {
                  e.stopPropagation();
                  config.launcherAction?.onClick?.();
                }}
                disabled={config.launcherAction.status === "loading"}
              >
                {config.launcherAction.status === "loading" ? (
                  <LoaderIcon />
                ) : (
                  <ArrowRightIcon />
                )}
                {config.launcherAction.tooltip && (
                  <span class="toggle-tooltip">
                    {config.launcherAction.tooltip}
                  </span>
                )}
              </button>
            )}
        </div>

        {showLauncherHint && (
          <div class="kbd-hint visible launcher-hint" aria-hidden="true">
            {launcherHint.keys.length > 0 && (
              <div class="kbd-group">
                {launcherHint.keys.map((key, index) => (
                  <div class="kbd" key={index}>
                    {key}
                  </div>
                ))}
              </div>
            )}
            <span class="kbd-text">{launcherHint.label}</span>
          </div>
        )}
      </div>
    </Fragment>
  );
};

// -- Main Class Wrapper (API compatible with old version) --

export class CueCard {
  private host: HTMLElement;
  private shadow: ShadowRoot;
  private config: CueCardConfig;
  private isOpen = false;
  private skipFocus = false;
  private mountTarget: HTMLElement | null = null;
  private deanonymizedCompany: DeanonymizedCompany | null = null;
  private deanonymizationStatus: "idle" | "pending" | "resolved" | "failed" =
    "idle";
  private deanonymizationCleanup: (() => void) | null = null;
  private hasStartedDeanonymization = false;

  constructor(config: CueCardConfig) {
    this.config = {
      position: "top-center",
      direction: "top-to-bottom",
      theme: "glass",
      entranceDelayMs: 0,
      exitDelayMs: 75,
      hasPersonalization: false,
      isVisible: false,
      progressPct: 0,
      status: "resting",
      enableTimer: true,
      timerDurationMs: 20_000,
      enableLauncher: true,
      enableFocusMode: false,
      focusBlurIntensity: "10px",
      focusDelayMs: 500,
      showKeyboardHints: false,
      showWatermark: true,
      mountMode: "overlay",
      ...config,
    };
    this.mountTarget = config.mountNode ?? null;
    if (this.isDeanonymizationEnabled()) {
      this.deanonymizationStatus = "pending";
    }

    this.host = document.createElement("div");
    this.host.id = "kenobi-cue-card-root";
    this.applyHostStyles();

    this.shadow = this.host.attachShadow({ mode: "open" });

    // Inject Styles once
    const style = document.createElement("style");
    style.textContent = STYLES;
    this.shadow.appendChild(style);

    // Create a mount point inside shadow DOM for Preact
    const mountPoint = document.createElement("div");
    this.shadow.appendChild(mountPoint);
  }

  private applyHostStyles() {
    const isInline = this.config.mountMode === "inline";
    this.host.classList.toggle("kb-inline", isInline);

    // Apply configurable CSS variables to the host
    const focusBlur = this.config.focusBlurIntensity ?? "10px";

    if (isInline) {
      this.host.style.cssText = `
        position: relative !important;
        z-index: auto !important;
        top: auto !important;
        left: auto !important;
        width: 100% !important;
        height: auto !important;
        pointer-events: auto !important;
        isolation: auto !important;
        display: block !important;
        --kb-focus-blur: ${focusBlur};
      `;
      return;
    }

    this.host.style.cssText = `
      position: fixed !important;
      z-index: 2147483647 !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      height: 0 !important;
      pointer-events: none !important;
      isolation: isolate !important;
      --kb-focus-blur: ${focusBlur};
    `;
  }

  public mount(target?: HTMLElement) {
    const resolvedTarget =
      target || this.config.mountNode || this.mountTarget || document.body;
    this.mountTarget = resolvedTarget;

    if (!resolvedTarget.contains(this.host)) {
      resolvedTarget.appendChild(this.host);
    }

    this.applyHostStyles();
    this.startDeanonymization();
    this.render();
  }

  public unmount() {
    // Cleanup Preact tree
    render(null, this.shadow.lastElementChild as HTMLElement);
    this.host.remove();
    this.deanonymizationCleanup?.();
    this.deanonymizationCleanup = null;
  }

  public update(newState: Partial<CueCardConfig>) {
    // Close form when transitioning to 'starting' status
    if (newState.status === "starting" && this.config.status !== "starting") {
      this.isOpen = false;
    }

    const wasDeanonymizationEnabled = this.isDeanonymizationEnabled();
    this.config = { ...this.config, ...newState };
    const isDeanonymizationEnabled = this.isDeanonymizationEnabled();

    if (isDeanonymizationEnabled && !wasDeanonymizationEnabled) {
      this.deanonymizationStatus = "pending";
      this.startDeanonymization();
    }
    if (!isDeanonymizationEnabled && wasDeanonymizationEnabled) {
      this.deanonymizedCompany = null;
      this.deanonymizationStatus = "idle";
      this.deanonymizationCleanup?.();
      this.deanonymizationCleanup = null;
      this.hasStartedDeanonymization = false;
    }

    this.applyHostStyles();
    this.render();
  }

  private isDeanonymizationEnabled() {
    return this.config.deanonymization?.provider === "Kenobi";
  }

  private startDeanonymization() {
    if (!this.isDeanonymizationEnabled()) return;
    if (this.hasStartedDeanonymization) return;
    if (this.deanonymizationStatus !== "pending") return;

    this.hasStartedDeanonymization = true;

    if (typeof window === "undefined") {
      this.deanonymizationStatus = "failed";
      this.render();
      return;
    }

    const cachedIdentity = window.__kenobiIdentityResolution as
      | IdentityResolutionDetail
      | null
      | undefined;
    if (cachedIdentity) {
      this.handleDeanonymizationResult(cachedIdentity);
      return;
    }

    const handleEvent = (event: Event) => {
      const detail = (event as CustomEvent<IdentityResolutionDetail>).detail;
      this.handleDeanonymizationResult(detail);
    };

    window.addEventListener(IDENTITY_RESOLVED_EVENT, handleEvent);
    ensureKenobiSnitcherLoaded(this.config.deanonymization);

    const timeoutMs =
      this.config.deanonymization?.timeoutMs ?? DEFAULT_DEANON_TIMEOUT_MS;
    const timeoutId = window.setTimeout(() => {
      window.removeEventListener(IDENTITY_RESOLVED_EVENT, handleEvent);
      this.handleDeanonymizationResult(null);
    }, timeoutMs);

    this.deanonymizationCleanup = () => {
      window.removeEventListener(IDENTITY_RESOLVED_EVENT, handleEvent);
      window.clearTimeout(timeoutId);
    };
  }

  private handleDeanonymizationResult(
    detail?: IdentityResolutionDetail | null
  ) {
    if (this.deanonymizationStatus !== "pending") return;

    this.deanonymizationCleanup?.();
    this.deanonymizationCleanup = null;

    const company = resolveDeanonymizedCompany(detail);
    this.deanonymizedCompany = company;
    this.deanonymizationStatus = company ? "resolved" : "failed";
    if (company) {
      dispatchCueCardEvent("deanonymization:success", {
        company,
        detail,
      });
    } else {
      dispatchCueCardEvent("deanonymization:fail", {
        detail,
      });
    }
    this.render();
  }

  private getRenderConfig() {
    if (this.deanonymizationStatus === "pending") {
      return { ...this.config, isVisible: false };
    }
    return this.config;
  }

  private clearDeanonymizedCompany = () => {
    const previousCompany = this.deanonymizedCompany;
    const shouldResetStatus =
      this.config.status === "success" || this.config.isPersonalized;

    if (this.config.isPersonalized) {
      this.config.onTogglePersonalization?.();
    }

    this.deanonymizedCompany = null;
    this.deanonymizationStatus = "failed";
    if (previousCompany) {
      dispatchCueCardEvent("deanonymization:not-you", {
        company: previousCompany,
      });
    }

    if (shouldResetStatus) {
      this.config = {
        ...this.config,
        status: "resting",
        progressPct: 0,
        isPersonalized: false,
      };
    }

    this.render();
  };

  public setTheme(theme: CueCardTheme) {
    this.update({ theme });
  }

  public openForm(skipFocus?: boolean) {
    this.isOpen = true;
    this.skipFocus = skipFocus ?? false;
    this.render();
  }

  private render() {
    // We render the Preact component into the shadow root mount point
    const mountPoint = this.shadow.lastElementChild as HTMLElement;
    const skipFocusOnce = this.skipFocus;
    this.skipFocus = false; // Reset after reading

    render(
      <CueCardContent
        config={this.getRenderConfig()}
        isOpen={this.isOpen}
        skipFocus={skipFocusOnce}
        deanonymizedCompany={this.deanonymizedCompany}
        onClearDeanonymizedCompany={this.clearDeanonymizedCompany}
        setIsOpen={(val) => {
          this.isOpen = val;
          this.config.onOpenChange?.(val);
          this.render(); // Re-render on state change since we are outside the react tree
        }}
      />,
      mountPoint
    );
  }
}
